import AES from 'crypto-js/aes';
import EncBase64 from 'crypto-js/enc-base64';
import EncUtf8 from 'crypto-js/enc-utf8';
import FormatHex from 'crypto-js/format-hex';
import ModeECB from 'crypto-js/mode-ecb';
import PadPKCS7 from 'crypto-js/pad-pkcs7';
import cookie from 'react-cookies';

import { EGlobalCookieKey } from '@/types/cookie';

let cacheAESKey: string = '';
let forgeLoadingPromise: Promise<any> | null = null;

// Preload node-forge immediately on module load (non-blocking)
if (typeof window !== 'undefined') {
  forgeLoadingPromise = import('node-forge').then((module) => module.default);
}

// Get the preloaded forge module
const loadForge = () => {
  if (!forgeLoadingPromise) {
    // Fallback if not preloaded (shouldn't happen in browser)
    forgeLoadingPromise = import('node-forge').then((module) => module.default);
  }

  return forgeLoadingPromise;
};

const getAESKey = async (): Promise<string> => {
  if (cacheAESKey) {
    return cacheAESKey;
  }

  const key = (cookie.load(EGlobalCookieKey.encrypt_key) as string) || '';

  if (!key) return key;

  const privateKeyString = `-----BEGIN RSA PRIVATE KEY-----
MIIEvQIBADANBgkqhkiG9w0BAQEFAASCBKcwggSjAgEAAoIBAQClgmK3i1CGadBM+N98Pj1iS5JHbi0Beyg+uiS2qy4UzCBRYCLcYpuy1NXbJUPHJ/lL1YCo0rhz43SEydXAGtsJ/00ewgxGyaR7RakkTH8Vaqpocd5KG5IMcywOupOwY9butQsYgj1A8xBmegpVb5uT6rhY8Th5qgujc+UC4qng8kvVCgHlHPFQRvYwlIGER19ZItoITjfMOGdhqfnrkam/UGsxl8KY01/qtZT/x5fYZGcE1zJMTPYkFhcEdolHwqpCV1z8qFx4b0UO/UuhMnt+AuwQugEON0Kgrd5KCYQ6OYly2X67W5V0EnFMqJl0VCjc40IsEOH2c7lSCHlQkcEtAgMBAAECggEAcit3HVWkvpPPdMlYoMPuZBftWAjQUu053ctZ+AgtSXtl77x5T71V2g6nVvxlvMP+XClzDwIM6yREBDWWlWXxIvxRnp4NCeee5sou2zNJluR1mIaz6DsMjDoGckKSDmNQqGmKteVhi6Qf9BDLgBP1JhwL/o6nfoWmcaJIn6zrspdVP+zn6a+RjJiPcYLljacmsVzz572Riw6K+eIsuyNcYcSWJtFbIVZhhJRU+1CykvL0rcZUMxFAlak4LTDZ4QODxj+xXxDp3qBU7G5caWogdRF5sx23po6mXf2NM66etCqdqb6XUjpUMqyofkI777dJ7eF52pljHRqWX3JDZi1wAQKBgQDVHg4DWeRFYI95cIU03fSgsddfxm1GCuazIDKP6/ZtTc89JwTT4b+xQHD2QeytuFiTXmaXWzQaeR6ACNk8tT5X671lxd3mgEvYXnY22UgPXCJTt8pnO47YGx8NaWE1JGGM+4yF8/COG4QXT+pJlmjsla0CmRcz+i8E2YuXC8jrgQKBgQDGz/qLqqnoWGWmR7mjoDnlEQImSWPzHFRu8RJxwqE3+YpJM/uF6sHxGyB14agZFRjgMdDzaHtHLMqpp/tOMVBsjti18PfNcxMkInFgezpqJMQERoBelt6GFYzCX8CfWBzVniU8dIGwP2ikfflISlpZ6+jf37K0bUONf+nosvQbrQKBgQCOGZYaYeu03qeLv5zyHTSpmSsUL0hZNZeRxFwyXpFXXFRcYBH1/UZFt8QaTuazBoH48S7SqPta03ukv0GqGudQ1Fza6U4GDXGE+ac09zf3FeXFkHyizLF4Is1XsfYg6vsod+nAv2CcQHiY2q5LFsErTrmnS9CO1bhYMX3D3xW/AQKBgGK3UCNbWUQp6EcZJB8P5s9Lk7KpDDzhWzc+8jaUOnLNsBFLA1/qQeRS4NpHcq0JobFoauUBgvzDS9nTs9+Ocu2EPCyS+qgDtIRjqcILTeT4Xm/1BbZx2Qng12SIIN+944taHAkZnG+D/JR4jHeNm7E8hwPqUhRukHVJBz7o5HZ9AoGAByHUPjCOqDf65LYo1nda9ThzKj6f4psQjj4l2NyS13in+5VwIVa9hDSedkwFy3tw7u26SJtqDU5LdYwx2t9ay777ob4GOKqBU8AIB+rafvpigCsaDvRbBb0zogpnf9eRR5Q+mRSyJuCkXlMvEMsDK8RKLhaCCzsLIDvJ8KgnBmo=
-----END RSA PRIVATE KEY-----`;

  try {
    // Dynamically load node-forge only when needed
    const forge = await loadForge();

    const privateKey = forge.pki.privateKeyFromPem(privateKeyString);
    const decrypted = privateKey.decrypt(forge.util.decode64(key), 'RSAES-PKCS1-V1_5');

    cacheAESKey = decrypted;

    return decrypted;
  } catch (error) {
    console.error('Failed to decrypt AES key:', error);

    return '';
  }
};

typeof window !== 'undefined' ? ((window as any).getAESKey = getAESKey) : null;

// Wait for AES key to be ready
export const waitForAESKey = async (timeout = 3000): Promise<boolean> => {
  if (cacheAESKey) return true;

  const startTime = Date.now();

  while (!cacheAESKey && Date.now() - startTime < timeout) {
    await new Promise((resolve) => setTimeout(resolve, 50));
  }

  return !!cacheAESKey;
};

export const encrypt = (data: string): string => {
  // Use cached key since getAESKey is now async
  const key = cacheAESKey;

  if (!key) {
    console.warn('AES key not ready, returning unencrypted data');

    return data;
  }

  let _data = data;

  if (typeof data === 'object') {
    // 如果传入的data是json对象，先转义为json字符串
    try {
      _data = JSON.stringify(data);
    } catch (error) {
      console.log('error:', error);
    }
  }

  const keyHex = EncUtf8.parse(key);
  const encrypted = AES.encrypt(EncUtf8.parse(_data), keyHex, {
    mode: ModeECB,
    padding: PadPKCS7
  });

  return EncBase64.stringify(encrypted.ciphertext);
};

typeof window !== 'undefined' ? ((window as any).encrypt = encrypt) : null;

export const decrypt = (encryptedVal: string | undefined) => {
  if (typeof encryptedVal !== 'string') return encryptedVal;

  // Use cached key since getAESKey is now async
  const key = cacheAESKey;

  if (!encryptedVal || !key) return encryptedVal;

  try {
    const ciphertext = EncBase64.parse(encryptedVal); // Base64解密
    const stringEncryptData = FormatHex.parse(ciphertext.toString());
    const keyHex = EncUtf8.parse(key);
    const decryptData = AES.decrypt(stringEncryptData, keyHex, {
      mode: ModeECB,
      padding: PadPKCS7
    });
    const decryptedStr = decryptData.toString(EncUtf8);

    return JSON.parse(decryptedStr);
  } catch (e) {
    console.error(encryptedVal, e);

    return encryptedVal;
  }
};

typeof window !== 'undefined' ? ((window as any).decrypt = decrypt) : null;

// Initialize AES key on module load (preload node-forge)
if (typeof window !== 'undefined') {
  getAESKey().catch((error) => {
    console.error('Failed to initialize AES key:', error);
  });
}
